import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { AppState, history } from '@app/store';
import { CatalogueModel } from '@models/Catalogue/Catalogue.model';
import { CategoryModel } from '@models/Category/Category.model';
import { ServiceGroupModel } from '@models/ServiceGroup/ServiceGroup.model';
import { ServiceModel } from '@models/Service/Service.model';
import {
  getCatalogueByRoute,
  getCatalogueElement,
} from '@app/services/api.service';
import { isRootElement, getRootTitleByUrl } from '@app/helpers/utils';
import {
  updateCatalogue,
  addCategory,
  addService,
  addLevel,
  clearServices,
  clearLevels,
  clearCategories,
  updateLevels,
  setLastOpenedCategory,
  startLoading,
  endLoading,
  addGroup,
  clearGroups,
  setBlocked,
  setAnimationDisabled,
  addExpandedElement,
  clearExpandedElements,
  updatePaths,
  initIsPopular,
  setIsPopular,
} from './actions';
import { setCurrentRoute, setPrevRoute } from '@app/store/stats/actions';
import { sendRouteEvent } from '@app/store/stats/thunks';
import { isNumeric } from '@app/services/utils';
import { Category } from '@app/models/Category/Category.interface';
import { Dispatch } from 'react';
import { AnyAction } from 'redux';
import { isPopularVisible } from '@app/utils/categories';

/**
 * Перейти в категорию (загрузив её данные и данные под-элементов)
 *
 * @param elementId Идентификатор категории
 */
export const openCategory =
  (
    elementId: number,
    isPopular?: boolean
  ): ThunkAction<void, AppState, null, Action<string>> =>
  async (dispatch, getState) => {
    dispatch(blockRoute());
    dispatch(clearExpandedElements());
    dispatch(setAnimationDisabled(false));

    const catalogueType = getState().catalogue.type;

    setTimeout(() => {
      dispatch(
        setPrevRoute(
          getState().stats.currentRouteTitle,
          getState().stats.currentRouteUrl
        )
      );
      if (
        Object.prototype.hasOwnProperty.call(
          getState().catalogue.categories,
          elementId
        )
      ) {
        dispatch(
          setCurrentRoute(
            getState().catalogue.categories[elementId].title,
            `${window.location.origin}${getState().router.location.pathname}`
          )
        );
      }
      dispatch(sendRouteEvent(elementId));
    });

    // Если открываем корень каталога, то вместо обновления категории обновляем весь каталог
    if (isRootElement(elementId)) {
      dispatch(updateCatalogueByRoute(`/${catalogueType}`));
      return;
    }

    const rootCategories = getState().catalogue.rootCategories;

    dispatch(startLoading());
    const elementResponse: any = await getCatalogueElement(
      catalogueType,
      elementId
    );

    dispatch(clearGroups());
    dispatch(clearServices());
    dispatch(clearLevels());

    let lockedCategories = [...rootCategories];
    let categories: number[] = [];

    let routeLink = '/' + catalogueType;
    if (
      elementResponse.parentId &&
      elementResponse.parentId !== 1 &&
      elementResponse.parentId !== 2
    ) {
      routeLink =
        getState().catalogue.categories[elementResponse.parentId].routePath;
    }

    let catalogue = addCatalogueElement(
      dispatch,
      {
        ...elementResponse,
        isPopularGlobal:
          elementResponse.id === getState().catalogue.rootCategories[0],
      },
      routeLink,
      elementResponse?.children?.length === 0
    );

    // Собираем массив категорий, которые не нужно удалять
    if (catalogue.lockedCategories)
      lockedCategories = [
        ...lockedCategories,
        ...catalogue.lockedCategories,
        catalogue.id,
      ];

    categories.push(catalogue.id);

    let newLevels: any[] = [];

    let paths: string[] = [];
    let path = '/';
    getState()
      .router.location.pathname.slice(1, -1)
      .split('/')
      .forEach((slug) => {
        // имя категории, а значит и её путь не могут состоять только из чисел. Если мы здесь получаем число, значит это группа или услуга
        if (!isNumeric(slug)) {
          path += slug + '/';
          paths.push(path);
        }
      });
    paths.forEach((path) => {
      let category = getState().catalogue.categoriesByPath[path];
      category?.id &&
        newLevels.push(category?.id) &&
        lockedCategories.push(category?.id);
      if (category?.children && category.children.length > 0) {
        category?.children.forEach((childId) => {
          lockedCategories.push(childId);
        });
      }
    });

    let lastCategory =
      getState().catalogue.categories[newLevels[newLevels.length - 1]];
    if (lastCategory?.children && lastCategory.children.length > 0) {
      newLevels.push(
        getState().catalogue.categories[lastCategory.id].children[0]
      );
    }
    dispatch(setAnimationDisabled(true));
    dispatch(clearCategories(lockedCategories));
    dispatch(updateLevels(newLevels));
    dispatch(updatePaths(paths));
    dispatch(setLastOpenedCategory(getState().catalogue));

    if (isPopular !== undefined) {
      dispatch(setIsPopular(isPopular));
    } else {
      const fromRoute = getState().catalogue.fromRoute;
      const urlParams = new URLSearchParams(window.location?.search);
      const activeTab = urlParams.get('tab');

      if (isPopularVisible(fromRoute, activeTab)) {
        dispatch(initIsPopular(getState().catalogue));
      } else {
        dispatch(setIsPopular(false));
      }
    }
    dispatch(endLoading());
    dispatch(setAnimationDisabled(false));
  };

/**
 * Проверяем не является ли элемент категорией, если нет, то помечаем как расхлопнутый
 */
export const searchExpandedElementBySlug =
  (slug: string): ThunkAction<void, AppState, null, Action<string>> =>
  async (dispatch, getState) => {
    let categories = getState().catalogue.categories;
    let catKeys = Object.keys(categories);
    let founded = false;
    for (let i = 0; i < catKeys.length; i++) {
      if (categories[catKeys[i]].slug === slug) {
        founded = true;
      }
    }
    if (!founded && slug !== getState().catalogue.type) {
      dispatch(addExpandedElement(parseInt(slug, 10)));
    }
  };

/**
 * Определяем расхлопнутые элементы по роутингу
 */
export const setExpandedElements =
  (route: string): ThunkAction<void, AppState, null, Action<string>> =>
  async (dispatch, getState) => {
    let routePath = route || getState().router.location.pathname;
    let routeArray = routePath.slice(1).split('/');
    if (routeArray[routeArray.length - 1] === '') {
      routeArray.pop();
    }
    for (let i = 0; i < routeArray.length; i++) {
      dispatch(searchExpandedElementBySlug(routeArray[i]));
    }
  };

export const setPopular =
  (isPopular: boolean): ThunkAction<void, AppState, null, Action<string>> =>
  async (dispatch, getState) => {
    // Если мы переходим из популярного в категории, то нужно проверить что и категория сменилась с "популярного" на родителя
    if (!isPopular) {
      let currentCategory = getState().catalogue.lastOpenedCategory;
      let parentCategory = currentCategory?.parentId
        ? getState().catalogue.categories[currentCategory.parentId]
        : null;
      if (
        parentCategory?.children &&
        parentCategory?.children[0] === currentCategory?.id
      ) {
        dispatch(openCategory(parentCategory.id));
        history.push(parentCategory.routePath);
      }
    }
    dispatch(setIsPopular(isPopular));
  };

/**
 * Обновление элементов каталога по пути роутинга
 * @param {string} route Путь роутинга
 */
export const updateCatalogueByRoute =
  (
    route: string = '',
    initialState?: any,
    disableAnimation?: boolean
  ): ThunkAction<void, AppState, null, Action<string>> =>
  async (dispatch, getState) => {
    setTimeout(() => {
      dispatch(
        setPrevRoute(
          getState().stats.currentRouteTitle,
          getState().stats.currentRouteUrl
        )
      );
      dispatch(
        setCurrentRoute(
          getRootTitleByUrl(route),
          `${window.location.origin}${getState().router.location.pathname}`
        )
      );
    });
    dispatch(blockRoute());
    let catalogueResponse;
    if (route && !initialState) {
      dispatch(startLoading());
      catalogueResponse = await getCatalogueByRoute(route);
    } else if (initialState) {
      catalogueResponse = initialState;
    }

    // Отключаем анимацию под-категорий 2го+ уровня, если перешли по кнопкам
    // "назад" или "вперед"
    if (disableAnimation) {
      dispatch(setAnimationDisabled(true));
    } else if (!disableAnimation && getState().catalogue.animationDisabled) {
      dispatch(setAnimationDisabled(false));
    }

    const catalogueType = catalogueResponse.entity.type;
    const categories: number[] = [];
    // Очистка стейта от старых услуг и очистка уровней
    dispatch(clearGroups());
    dispatch(clearServices());
    dispatch(clearLevels());

    // Добавленные категории, которые нужно сохранить в стейте
    let lockedCategories: any[] = [];

    // Обработка дочерних элементов
    if (catalogueResponse.children && catalogueResponse.children.length) {
      catalogueResponse.children.forEach((category: Category, id: number) => {
        let catalogue = addCatalogueElement(
          dispatch,
          { ...category, isPopularGlobal: id === 0 },
          '/' + catalogueType
        );

        if (catalogue.lockedCategories)
          lockedCategories = [
            ...lockedCategories,
            ...catalogue.lockedCategories,
          ];

        categories.push(category.id);
      });
    }

    let paths: string[] = [];
    let path = '/';
    getState()
      .router.location.pathname.slice(1, -1)
      .split('/')
      .forEach((slug) => {
        // имя категории, а значит и её путь не могут состоять только из чисел. Если мы здесь получаем число, значит это группа или услуга
        if (!isNumeric(slug)) {
          path += slug + '/';
          paths.push(path);
        }
      });

    // Обновление стейта каталога
    dispatch(
      updateCatalogue(new CatalogueModel(catalogueResponse.entity, categories))
    );

    // Удаление категорий, которые не используются в обновленном стейте
    dispatch(clearCategories(lockedCategories));

    dispatch(updatePaths(paths));
    const fromRoute = getState().catalogue.fromRoute;
    const urlParams = new URLSearchParams(window.location?.search);
    const activeTab = urlParams.get('tab');
    if (isPopularVisible(fromRoute, activeTab)) {
      dispatch(initIsPopular(getState().catalogue));
    }
    dispatch(setLastOpenedCategory(getState().catalogue));

    setTimeout(() => {
      dispatch(
        setCurrentRoute(
          getState().catalogue.lastOpenedCategory!.title,
          `${window.location.origin}${getState().router.location.pathname}`
        )
      );
    });
    dispatch(setExpandedElements(route));

    dispatch(endLoading());
    dispatch(setAnimationDisabled(false));
  };

/**
 * Ставим флаг, который блокирует переключение роутинга на 50мс, после активации перехода
 */
export const blockRoute =
  (): ThunkAction<void, AppState, null, Action<string>> =>
  async (dispatch, getState) => {
    if (!getState().catalogue.isBlocked) {
      dispatch(setBlocked(true));
      setTimeout(() => {
        dispatch(setBlocked(false));
      }, 50);
    }
  };

/**
 * Добавление элемента в стейт каталога
 * @param dispatch Dispatch для вызова действий стейта
 * @param element JSON-элемента, который необходимо добавить
 * @param routePath Путь роутинга для создания ссылки
 *
 * @returns {any | boolean} Добавленный элемент или false
 */
function addCatalogueElement(
  dispatch: Dispatch<AnyAction>,
  element: any,
  routePath: string = '',
  skipLevels: boolean = false
): any | boolean {
  // Массив категорий, которые будут добавлены (с учетом children)
  let lockedCategories = element.lockedCategories
    ? element.lockedCategories
    : [];
  element.elementsType = '';

  if (element.discr === 'category') {
    // Создание ссылки для роутинга
    element.routePath =
      routePath +
      `${routePath.substr(-1, 1) === '/' ? '' : '/'}` +
      element.entity.slug +
      '/';

    // Если у категории есть родитель, то добавляем id в массив
    if (element.parentId != null) lockedCategories.push(element.id);
  } else if (element.discr === 'group') {
    element.routePath =
      routePath +
      `${routePath.substr(-1, 1) === '/' ? '' : '/'}` +
      element.entity.id +
      '/';
    element.openedRoutePath = routePath;
    dispatch(addGroup(new ServiceGroupModel(element)));
    return false;
  }

  // Если есть дочерние элементы, то их тоже добавляем
  if (element.children && element.children.length) {
    const childrens: any[] = [];
    element.children.forEach((children: any) => {
      // Раскомментировать для обновленного роутинга на бэке
      let childrenElement = addCatalogueElement(
        dispatch,
        { ...children, isPopularGlobal: element.isPopularGlobal },
        element.routePath
      );
      // let childrenElement = addCatalogueElement(dispatch, children, routePath);
      if (childrenElement) {
        childrens.push(childrenElement.id);
        // Если у дочернего элемента есть добавленные категории, переносим их в родителя
        if (childrenElement.lockedCategories)
          lockedCategories = [
            ...lockedCategories,
            ...childrenElement.lockedCategories,
          ];
        if (element.isPopularGlobal) {
          element.elementsType = 'mixed';
        } else if (!element.elementsType) {
          element.elementsType = 'categories';
        }
      } else {
        if (element.isPopularGlobal) {
          element.elementsType = 'mixed';
        } else if (!element.elementsType) {
          element.elementsType = 'services';
        }
      }
    });
    element.children = childrens;
    // Т.к. у элемента есть дочерние, значит он открыт и нужно добавить уровень вложенности
    if (!skipLevels) {
      dispatch(addLevel(element.id));
    }
  }

  if (element.discr === 'category') {
    dispatch(addCategory(new CategoryModel(element)));
  } else if (element.discr === 'service') {
    if (element.entity.subServices) {
      element.routePath =
        routePath +
        `${routePath.substr(-1, 1) === '/' ? '' : '/'}` +
        element.id +
        '/';
      element.openedRoutePath = routePath;
    }
    dispatch(addService(new ServiceModel(element)));
    return false;
  }

  element.lockedCategories = lockedCategories;

  return element;
}
